// Account & security (`/account`, from the account menu; coverage matrix rows 12, 93, 94):
// - Profile: the name on the account (Better Auth `update-user`), the sign-in email, and the
//   caller's own membership in each company (`GET …/people/me`: name there, roles, status);
// - Password: change it (`change-password`, optionally ending the other sessions). Accounts made
//   by an email link have no password and set their first one here (`set-password`);
// - Two-step sign-in (HARDEN-mfa, mfa.tsx): set up an authenticator app, new recovery codes,
//   turn it off;
// - Sessions: every signed-in device (`list-sessions`), "Sign out" one, or all the others
//   (`revoke-session`, `revoke-other-sessions`), each confirmed first.
// "Forgot password" (row 95) is on the sign-in page and `/reset-password`.
// SETUP-UX: the 56 px location header (Account › Account & security), then `SettingsGroup`s of
// label-left / control-right rows; a device's "Sign out" shows on hover or focus.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  FieldError,
  Input,
  Notice,
  SettingsGroup,
  SettingsRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api, AuthSession } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { formatDate, formatDateTime } from "../format";
import { PersonalMemoryConsent } from "../memory/personal-consent";
import { ProfileMemory } from "../memory/profile-memory";
import { LoadError, Loading } from "../screen-parts";
import { ROLE_LABELS } from "../settings/people";
import { SetupPage } from "../settings/setup-page";
import { featureState } from "../use-feature";
import { TwoStepSection } from "./mfa";
import { OutOfOffice } from "./out-of-office";

const accountKeys = {
  sessions: ["account", "sessions"] as const,
  password: ["account", "has-password"] as const,
};

/** A short, readable device name from a user agent. */
export function deviceOf(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : null;
  const os = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser ?? os ?? ua.slice(0, 40);
}

/** The right-hand column of a field row: the input and its error under it. */
function Control({ children }: { children: ReactNode }) {
  return <div className="flex w-64 flex-col gap-1 max-md:w-full">{children}</div>;
}

/** A group's foot: the submit button (and a failure) on the right. */
function Foot({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-subtle px-4 py-2.5">
      {children}
    </div>
  );
}

export function AccountScreen({ api, me }: { api: Api; me: MeResponse }) {
  return (
    <SetupPage crumbs={["Your settings"]} title="Account & security">
      <ProfileSection api={api} me={me} />
      <PasswordSection api={api} />
      <TwoStepSection api={api} me={me} />
      <SessionsSection api={api} />
      {/* RECALL-PEOPLE-SURFACES: what memory knows about me, fix, forget, holds, Remember my DMs */}
      {me.memberships
        .filter((m) => m.kind === "person" && featureState(me, m.company_id, "memory").enabled)
        .map((m) => (
          <ProfileMemory
            key={m.company_id}
            api={api}
            co={m.company_id}
            id={`profile-memory-${m.company_slug}`}
            title={me.memberships.length > 1 ? `Memory · ${m.company_name}` : "Memory"}
          />
        ))}
      {/* OOO-DELEGATE: dates and a delegate per company I am a person in (absent while off) */}
      {me.memberships
        .filter((m) => m.kind === "person")
        .map((m) => (
          <OutOfOffice
            key={m.company_id}
            api={api}
            co={m.company_id}
            me={m.membership_id}
            id={`out-of-office-${m.company_slug}`}
            title={
              me.memberships.length > 1 ? `Out of office · ${m.company_name}` : "Out of office"
            }
          />
        ))}
      {/* D12: my agents' read access to my personal memory, per company I am a person in */}
      {me.memberships
        .filter((m) => m.kind === "person")
        .map((m) => (
          <PersonalMemoryConsent
            key={m.company_id}
            api={api}
            co={m.company_id}
            id={`personal-memory-${m.company_slug}`}
            title={
              me.memberships.length > 1
                ? `Your personal memory · ${m.company_name}`
                : "Your personal memory"
            }
          />
        ))}
    </SetupPage>
  );
}

function ProfileSection({ api, me }: { api: Api; me: MeResponse }) {
  const qc = useQueryClient();
  const [name, setName] = useState(me.user.name);
  const save = useMutation({
    mutationFn: () => api.auth.updateName(name.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.add({ title: "Name saved", type: "success" });
    },
  });
  const memberships = me.memberships;
  const mine = useQueries({
    queries: memberships.map((m) => ({
      queryKey: ["company", m.company_id, "people", "me"],
      queryFn: () => api.client.people.me({ params: { co: m.company_id } }),
    })),
  });
  return (
    <>
      <SettingsGroup id="profile" title="Profile">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && name.trim() !== me.user.name) save.mutate();
          }}
        >
          <SettingsRow label="Name" htmlFor="account-name">
            <Control>
              <div className="flex items-center gap-2">
                <Input
                  id="account-name"
                  className="h-8"
                  autoComplete="name"
                  value={name}
                  aria-invalid={!name.trim() || undefined}
                  onChange={(e) => setName(e.target.value)}
                />
                <Button
                  type="submit"
                  size="sm"
                  className="shrink-0"
                  disabled={save.isPending || !name.trim() || name.trim() === me.user.name}
                >
                  {save.isPending ? "Saving…" : "Save name"}
                </Button>
              </div>
              {!name.trim() ? <FieldError>Enter your name.</FieldError> : null}
            </Control>
          </SettingsRow>
        </form>
        <SettingsRow label="Email" hint="The address you sign in with." htmlFor="account-email">
          <Control>
            <Input id="account-email" className="h-8" value={me.user.email} readOnly disabled />
          </Control>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Your companies" count={memberships.length}>
        {memberships.length ? (
          <ul className="flex flex-col" data-slot="my-memberships">
            {memberships.map((m, i) => {
              const q = mine[i];
              return (
                <li key={m.company_id} className="border-t border-subtle first:border-t-0">
                  <SettingsRow
                    className="border-t-0"
                    label={m.company_name}
                    hint={
                      q?.data
                        ? `Listed as ${q.data.display_name} · ${q.data.status} · joined ${formatDate(q.data.joined_at)}`
                        : q?.isError
                          ? "Membership details unavailable"
                          : "Loading…"
                    }
                  >
                    <span className="flex flex-wrap gap-1">
                      {m.roles.map((r) => (
                        <Badge key={r} variant="outline">
                          {ROLE_LABELS[r] ?? r}
                        </Badge>
                      ))}
                    </span>
                  </SettingsRow>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">
            {me.agency
              ? "You reach client companies through your agency."
              : "You are not a member of any company yet."}
          </p>
        )}
      </SettingsGroup>
    </>
  );
}

function PasswordSection({ api }: { api: Api }) {
  const has = useQuery({ queryKey: accountKeys.password, queryFn: () => api.auth.hasPassword() });
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [others, setOthers] = useState(true);
  const [touched, setTouched] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      api.auth.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: others,
      }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setTouched(false);
      void qc.invalidateQueries({ queryKey: accountKeys.sessions });
      toast.add({ title: "Password changed", type: "success" });
    },
  });
  const short = next.length < 8;
  const mismatch = next !== confirm;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (current && !short && !mismatch) change.mutate();
  };
  return (
    <SettingsGroup id="password" title="Password">
      {has.isPending ? (
        <div className="p-4">
          <Loading label="Loading password settings" rows={1} />
        </div>
      ) : has.isError ? (
        <div className="p-4">
          <LoadError what="Password settings" error={has.error} />
        </div>
      ) : !has.data ? (
        <SetPasswordForm api={api} />
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <SettingsRow label="Current password" htmlFor="password-current">
            <Control>
              <Input
                id="password-current"
                type="password"
                autoComplete="current-password"
                className="h-8"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Control>
          </SettingsRow>
          <SettingsRow label="New password" hint="8 characters or more." htmlFor="password-new">
            <Control>
              <Input
                id="password-new"
                type="password"
                autoComplete="new-password"
                className="h-8"
                value={next}
                aria-invalid={(touched && short) || undefined}
                onChange={(e) => setNext(e.target.value)}
              />
              {touched && short ? <FieldError>At least 8 characters.</FieldError> : null}
            </Control>
          </SettingsRow>
          <SettingsRow label="Repeat the new password" htmlFor="password-confirm">
            <Control>
              <Input
                id="password-confirm"
                type="password"
                autoComplete="new-password"
                className="h-8"
                value={confirm}
                aria-invalid={(touched && mismatch) || undefined}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {touched && mismatch ? <FieldError>The passwords differ.</FieldError> : null}
            </Control>
          </SettingsRow>
          <SettingsRow label="Sign out my other devices" htmlFor="password-others">
            <Checkbox
              id="password-others"
              checked={others}
              onCheckedChange={(v) => setOthers(v === true)}
            />
          </SettingsRow>
          <Foot>
            {change.isError ? (
              <Notice tone="danger" role="alert" className="mr-auto">
                {change.error instanceof Error ? change.error.message : "Not changed."}
              </Notice>
            ) : null}
            <Button type="submit" size="sm" disabled={change.isPending}>
              {change.isPending ? "Changing…" : "Change password"}
            </Button>
          </Foot>
        </form>
      )}
    </SettingsGroup>
  );
}

/** An account made by an email link has no password: this sets its first one. */
function SetPasswordForm({ api }: { api: Api }) {
  const qc = useQueryClient();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const set = useMutation({
    mutationFn: () => api.auth.setPassword(next),
    onSuccess: () => {
      setNext("");
      setConfirm("");
      setTouched(false);
      void qc.invalidateQueries({ queryKey: accountKeys.password });
      toast.add({ title: "Password set", type: "success" });
    },
  });
  const short = next.length < 8;
  const mismatch = next !== confirm;
  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (!short && !mismatch) set.mutate();
      }}
    >
      <SettingsRow
        label="You sign in with email links"
        hint="Set a password to sign in with it too; the email links keep working."
      />
      <SettingsRow label="New password" hint="8 characters or more." htmlFor="password-first">
        <Control>
          <Input
            id="password-first"
            type="password"
            autoComplete="new-password"
            className="h-8"
            value={next}
            aria-invalid={(touched && short) || undefined}
            onChange={(e) => setNext(e.target.value)}
          />
          {touched && short ? <FieldError>At least 8 characters.</FieldError> : null}
        </Control>
      </SettingsRow>
      <SettingsRow label="Repeat the new password" htmlFor="password-first-confirm">
        <Control>
          <Input
            id="password-first-confirm"
            type="password"
            autoComplete="new-password"
            className="h-8"
            value={confirm}
            aria-invalid={(touched && mismatch) || undefined}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {touched && mismatch ? <FieldError>The passwords differ.</FieldError> : null}
        </Control>
      </SettingsRow>
      <Foot>
        {set.isError ? (
          <Notice tone="danger" role="alert" className="mr-auto">
            {set.error instanceof Error ? set.error.message : "Not set."}
          </Notice>
        ) : null}
        <Button type="submit" size="sm" disabled={set.isPending}>
          {set.isPending ? "Setting…" : "Set password"}
        </Button>
      </Foot>
    </form>
  );
}

function SessionsSection({ api }: { api: Api }) {
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: accountKeys.sessions,
    queryFn: () => api.auth.listSessions(),
  });
  const [ending, setEnding] = useState<AuthSession | "others" | null>(null);
  const end = useMutation({
    mutationFn: (target: AuthSession | "others") =>
      target === "others" ? api.auth.revokeOtherSessions() : api.auth.revokeSession(target.id),
    onSuccess: (_r, target) => {
      void qc.invalidateQueries({ queryKey: accountKeys.sessions });
      toast.add({
        title: target === "others" ? "Other devices signed out" : "Device signed out",
        type: "success",
      });
    },
  });
  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current);
  return (
    <SettingsGroup
      id="sessions"
      title="Signed-in devices"
      {...(sessions.data ? { count: list.length } : {})}
      actions={
        <Button
          variant="ghost"
          size="sm"
          disabled={!others.length || end.isPending}
          onClick={() => setEnding("others")}
        >
          Sign out all others
        </Button>
      }
    >
      {sessions.isPending ? (
        <div className="p-4">
          <Loading label="Loading sessions" rows={2} />
        </div>
      ) : sessions.isError ? (
        <div className="p-4">
          <LoadError what="Sessions" error={sessions.error} />
        </div>
      ) : (
        <ul className="flex flex-col" data-slot="sessions">
          {list.map((s) => (
            <li
              key={s.id}
              data-session={s.id}
              className="group/session flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1 border-t border-subtle px-4 py-2 text-[13px] first:border-t-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{deviceOf(s.userAgent)}</span>
                  {s.current ? <Badge variant="success">This device</Badge> : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  Signed in {formatDateTime(s.createdAt)} · until {formatDate(s.expiresAt)}
                </p>
              </div>
              {s.current ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="opacity-0 group-focus-within/session:opacity-100 group-hover/session:opacity-100 pointer-coarse:opacity-100"
                  aria-label={`Sign out ${deviceOf(s.userAgent)}, signed in ${formatDateTime(s.createdAt)}`}
                  onClick={() => setEnding(s)}
                >
                  Sign out
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={ending !== null} onOpenChange={(o) => !o && setEnding(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {ending === "others" ? "Sign out every other device?" : "Sign out this device?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {ending === "others"
                ? "Every session except this one ends now; those devices must sign in again."
                : ending
                  ? `${deviceOf(ending.userAgent)} must sign in again to use CompanyOS.`
                  : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (ending) end.mutate(ending);
                setEnding(null);
              }}
            >
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsGroup>
  );
}
